import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { api, type RequestCapacity } from "../api/client";
import { session } from "../auth/session";
import { i18n } from "../i18n";

@customElement("settings-view")
export class SettingsView extends LitElement {
  @state() private capacity: RequestCapacity | null = null;
  @state() private execution = "10";
  @state() private queue = "0";
  @state() private loading = false;
  @state() private saving = false;
  @state() private error = "";
  @state() private success = "";
  private version = 0;
  private get administrator() {
    return session.user?.role === "administrator";
  }
  protected createRenderRoot() {
    return this;
  }
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("sessionchange", this.sessionChanged);
    window.addEventListener("languagechange", this.languageChanged);
    void this.load();
  }
  disconnectedCallback() {
    this.version += 1;
    window.removeEventListener("sessionchange", this.sessionChanged);
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }
  private languageChanged = () => this.requestUpdate();
  private sessionChanged = () => {
    this.version += 1;
    this.capacity = null;
    this.error = this.success = "";
    this.loading = this.saving = false;
    if (this.administrator) void this.load();
    this.requestUpdate();
  };
  private apply(value: RequestCapacity) {
    this.capacity = value;
    this.execution = String(value.long_request_limit);
    this.queue = String(value.max_queued_requests);
  }
  private async load() {
    if (!this.administrator || this.saving) return;
    const version = ++this.version;
    this.loading = true;
    this.error = this.success = "";
    try {
      const result = await api.requestCapacity();
      if (version === this.version && this.administrator) this.apply(result);
    } catch (error) {
      if (version === this.version) this.error = (error as Error).message;
    } finally {
      if (version === this.version) this.loading = false;
    }
  }
  private async save(event?: Event) {
    event?.preventDefault();
    if (!this.administrator || !this.capacity || this.saving || this.loading)
      return;
    this.error = this.success = "";
    const execution = Number(this.execution);
    const queue = Number(this.queue);
    if (
      !/^\d+$/.test(this.execution) ||
      !/^\d+$/.test(this.queue) ||
      !Number.isInteger(execution) ||
      execution < this.capacity.minimum ||
      execution > this.capacity.maximum ||
      !Number.isInteger(queue) ||
      queue < this.capacity.queue_minimum ||
      queue > this.capacity.queue_maximum
    ) {
      this.error = i18n.t("capacityInvalid");
      return;
    }
    const version = ++this.version;
    this.saving = true;
    try {
      const result = await api.updateRequestCapacity({
        long_request_limit: execution,
        max_queued_requests: queue,
      });
      if (version === this.version && this.administrator) {
        this.apply(result);
        this.success = i18n.t("capacitySaved");
      }
    } catch (error) {
      if (version === this.version) this.error = (error as Error).message;
    } finally {
      if (version === this.version) this.saving = false;
    }
  }
  render() {
    if (!this.administrator)
      return html`<p class="status status-error">
        ${i18n.t("settingsAdminOnly")}
      </p>`;
    const busy = this.loading || this.saving;
    return html`<page-heading
        heading=${i18n.t("settings")}
        description=${i18n.t("settingsDescription")}
      ></page-heading>
      <surface-window heading=${i18n.t("capacityTitle")}>
        <p>${i18n.t("capacityHelp")}</p>
        ${this.error ? html`<p class="status status-error" role="alert">${this.error}</p>` : nothing}
        ${this.success ? html`<p class="status status-success" role="status">${this.success}</p>` : nothing}
        <form class="form-grid task-editor-fields" @submit=${this.save}>
          <label class="form-field"
            ><span>${i18n.t("capacityExecution")}</span>
            <input
              class="a2k-native-input"
              type="number"
              min=${this.capacity?.minimum ?? 10}
              max=${this.capacity?.maximum ?? 100}
              step="1"
              required
              .value=${this.execution}
              ?disabled=${busy || !this.capacity}
              @input=${(event: Event) => {
                this.execution = (event.target as HTMLInputElement).value;
              }}
            />
          </label>
          <label class="form-field"
            ><span>${i18n.t("capacityQueue")}</span>
            <input
              class="a2k-native-input"
              type="number"
              min=${this.capacity?.queue_minimum ?? 0}
              max=${this.capacity?.queue_maximum ?? 100}
              step="1"
              required
              .value=${this.queue}
              ?disabled=${busy || !this.capacity}
              @input=${(event: Event) => {
                this.queue = (event.target as HTMLInputElement).value;
              }}
            />
          </label>
          <div class="toolbar wide">
            <a2k-button type="submit" ?disabled=${busy || !this.capacity}
              >${i18n.t(this.saving ? "working" : "save")}</a2k-button
            >
            <a2k-button type="button" ?disabled=${busy} @click=${this.load}
              >${i18n.t(this.loading ? "working" : "refresh")}</a2k-button
            >
          </div>
        </form>
        ${
          this.capacity
            ? html`<dl class="detail-list">
                <dt>${i18n.t("capacityActive")}</dt>
                <dd>${this.capacity.active_requests}</dd>
                <dt>${i18n.t("capacityWaiting")}</dt>
                <dd>${this.capacity.queued_requests}</dd>
              </dl>`
            : nothing
        }
        <p>${i18n.t("capacityBehavior")}</p>
      </surface-window>`;
  }
}
